import { useState, useRef, useEffect, useCallback } from "react";
import { getNotebookById, updateNotebook } from "wasp/client/operations";
import { useQuery, useAction } from "wasp/client/operations";
import { Card, CardContent } from "../../client/components/ui/card";
import { FileText, Pencil, Check } from "lucide-react";
import NotebookEditor from "../../notebook/LazyNotebookEditor";

interface NoteWidgetProps {
  notebookNoteId: string;
}

export default function NoteWidget({ notebookNoteId }: NoteWidgetProps) {
  const { data: note } = useQuery(
    getNotebookById,
    { id: notebookNoteId },
    { enabled: !!notebookNoteId },
  );
  const update = useAction(updateNotebook);

  const [isFocused, setIsFocused] = useState(false);
  const [content, setContent] = useState<string>("");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState("");
  const titleInputRef = useRef<HTMLInputElement>(null);
  const isFirstLoadRef = useRef(true);
  const pendingHtmlRef = useRef<string | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Load note content on first fetch, and accept background refetch updates
  // when the user has no pending unsaved edits
  useEffect(() => {
    if (!note) return;
    if (!isFirstLoadRef.current && pendingHtmlRef.current !== null) return;
    setContent(note.content ?? "");
    isFirstLoadRef.current = false;
  }, [note?.id, note?.content]);

  const flushSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (pendingHtmlRef.current !== null) {
      update({ id: notebookNoteId, content: pendingHtmlRef.current });
      pendingHtmlRef.current = null;
    }
  }, [notebookNoteId, update]);

  const flushSaveRef = useRef(flushSave);
  useEffect(() => { flushSaveRef.current = flushSave; }, [flushSave]);

  const handleChange = useCallback((html: string) => {
    pendingHtmlRef.current = html;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      update({ id: notebookNoteId, content: html });
      pendingHtmlRef.current = null;
    }, 800);
  }, [notebookNoteId, update]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    if (wrapperRef.current && !wrapperRef.current.contains(e.relatedTarget as Node)) {
      flushSave();
      setIsFocused(false);
    }
  }, [flushSave]);

  // When activated, focus the ProseMirror after Tiptap re-renders as editable
  useEffect(() => {
    if (!isFocused) return;
    const id = setTimeout(() => {
      const pm = wrapperRef.current?.querySelector<HTMLElement>(".ProseMirror");
      if (pm) pm.focus();
    }, 0);
    return () => clearTimeout(id);
  }, [isFocused]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      flushSaveRef.current();
    };
  }, []);

  const startEditingTitle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setTitleValue(note?.title ?? "");
    setIsEditingTitle(true);
  }, [note?.title]);

  const commitTitleEdit = useCallback(() => {
    const trimmed = titleValue.trim();
    if (trimmed && trimmed !== note?.title) {
      update({ id: notebookNoteId, title: trimmed });
    }
    setIsEditingTitle(false);
  }, [titleValue, note?.title, notebookNoteId, update]);

  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") commitTitleEdit();
    if (e.key === "Escape") setIsEditingTitle(false);
  }, [commitTitleEdit]);

  // Auto-focus input when title editing starts
  useEffect(() => {
    if (isEditingTitle) titleInputRef.current?.select();
  }, [isEditingTitle]);

  if (!notebookNoteId) {
    return (
      <Card className="h-full">
        <CardContent className="p-3 h-full flex flex-col items-center justify-center gap-2 text-center">
          <FileText className="h-6 w-6 text-muted-foreground/40" />
          <p className="text-xs text-muted-foreground">Aucune note liée</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col overflow-hidden">
      {note && (
        <div className="px-3 pt-2 pb-1 shrink-0 flex items-center gap-1 group/title min-w-0">
          {isEditingTitle ? (
            <>
              <input
                ref={titleInputRef}
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                onBlur={commitTitleEdit}
                className="flex-1 min-w-0 text-xs font-medium bg-transparent border-b border-primary outline-none text-foreground"
                maxLength={200}
              />
              <button
                onMouseDown={(e) => { e.preventDefault(); commitTitleEdit(); }}
                className="shrink-0 text-primary hover:text-primary/80"
              >
                <Check className="h-3 w-3" />
              </button>
            </>
          ) : (
            <>
              <p
                className="text-xs font-medium text-muted-foreground min-w-0"
                style={{ overflow: "hidden", textOverflow: note.title.length > 30 ? "ellipsis" : "clip", whiteSpace: "nowrap" }}
                title={note.title.length > 30 ? note.title : undefined}
              >
                {note.title}
              </p>
              <button
                onClick={startEditingTitle}
                className="shrink-0 opacity-0 group-hover/title:opacity-100 transition-opacity text-muted-foreground hover:text-foreground ml-0.5"
                tabIndex={-1}
              >
                <Pencil className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
      )}
      <CardContent className="p-0 flex-1 overflow-hidden">
        <style>{`.note-widget .ProseMirror { min-height: 0 !important; }`}</style>
        <div
          ref={wrapperRef}
          className="note-widget h-full flex flex-col"
          tabIndex={-1}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={() => setIsFocused(true)}
        >
          <NotebookEditor
            content={content}
            onChange={handleChange}
            readOnly={!isFocused}
            className="h-full flex flex-col"
            contentClassName="p-3 flex-1 overflow-auto cursor-text"
            compact
          />
        </div>
      </CardContent>
    </Card>
  );
}
